import { RefObject } from "react";

export interface EnvironmentInfo {
  type: string;
  os: string;
  osVersion: string;
  browser: string;
  browserVersion: string;
  userAgent: string;
}

export interface DeviceInfo {
  screenWidth: number;
  screenHeight: number;
}

export function getEnvironmentInfo(): EnvironmentInfo {
  const userAgent = navigator.userAgent;

  // Mobile detection
  let type = "Desktop";
  if (/Mobi|Android/i.test(userAgent)) {
    type = "Mobile";
  } else if (/Tablet|iPad/i.test(userAgent)) {
    type = "Tablet";
  }

  // OS detection and version extraction
  let os = "Unknown";
  let osVersion = "Unknown";
  if (/Windows NT/.test(userAgent)) {
    os = "Windows";
    osVersion = (/Windows NT ([0-9.]+)/.exec(userAgent) || [])[1] || "Unknown";
  } else if (/Mac OS X/.test(userAgent)) {
    os = "MacOS";
    osVersion =
      (/Mac OS X ([0-9_]+)/.exec(userAgent) || [])[1]?.replace(/_/g, ".") ||
      "Unknown";
  } else if (/Linux/.test(userAgent)) {
    os = "Linux";
    osVersion =
      (/Linux ([a-zA-Z0-9._-]+)/.exec(userAgent) || [])[1] || "Unknown";
  } else if (/Android/.test(userAgent)) {
    os = "Android";
    osVersion = (/Android ([0-9.]+)/.exec(userAgent) || [])[1] || "Unknown";
  } else if (/iOS|iPad|iPhone|iPod/.test(userAgent)) {
    os = "iOS";
    osVersion =
      (/OS ([0-9_]+)/.exec(userAgent) || [])[1]?.replace(/_/g, ".") ||
      "Unknown";
  }

  // Browser detection and version extraction
  let browser = "Unknown";
  let browserVersion = "Unknown";
  if (/Chrome/.test(userAgent) && !/Edg/.test(userAgent)) {
    browser = "Chrome";
    browserVersion =
      (/Chrome\/([0-9.]+)/.exec(userAgent) || [])[1] || "Unknown";
  } else if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) {
    browser = "Safari";
    browserVersion =
      (/Version\/([0-9.]+)/.exec(userAgent) || [])[1] || "Unknown";
  } else if (/Firefox/.test(userAgent)) {
    browser = "Firefox";
    browserVersion =
      (/Firefox\/([0-9.]+)/.exec(userAgent) || [])[1] || "Unknown";
  } else if (/MSIE|Trident/.test(userAgent)) {
    browser = "Internet Explorer";
    browserVersion =
      (/(?:MSIE |rv:)([0-9.]+)/.exec(userAgent) || [])[1] || "Unknown";
  } else if (/Edg/.test(userAgent)) {
    browser = "Edge";
    browserVersion = (/Edg\/([0-9.]+)/.exec(userAgent) || [])[1] || "Unknown";
  } else {
    const unknownBrowserMatch = userAgent.match(
      /(Opera|OPR|YaBrowser|UCBrowser|SamsungBrowser|Vivaldi|Brave|Maxthon|Seamonkey)\/? ?([0-9.]*)/
    );
    if (unknownBrowserMatch) {
      browser = unknownBrowserMatch[1];
      browserVersion = unknownBrowserMatch[2] || "Unknown";
    }
  }

  return {
    type,
    os,
    osVersion,
    browser,
    browserVersion,
    userAgent,
  };
}

export function getDeviceInfo(): DeviceInfo {
  return {
    screenWidth: screen.width,
    screenHeight: screen.height,
  };
}

export function getRefSize(ref: RefObject<HTMLVideoElement | HTMLDivElement>) {
  return {
    width: ref.current?.getBoundingClientRect().width || "unknown",
    height: ref.current?.getBoundingClientRect().height || "unknown",
  };
}

export function isInApp() {
  const userAgent = navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipod|ipad/.test(userAgent);
  const isAndroid = /android/.test(userAgent);

  const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);
  const isIOSWebView =
    isIOS &&
    !isSafari &&
    !("standalone" in window.navigator && (window.navigator as any).standalone);
  const isAndroidWebView = isAndroid && userAgent.includes("wv");

  return isIOSWebView || isAndroidWebView;
}

export function getBrowserName() {
  const userAgent = navigator.userAgent;

  let browserName = "Unknown";
  if (/firefox|fxios/i.test(userAgent)) {
    browserName = "Firefox";
  } else if (/chrome|crios/i.test(userAgent)) {
    browserName = "Chrome";
  } else if (/safari/i.test(userAgent) && !/chrome|crios/i.test(userAgent)) {
    browserName = "Safari";
  } else if (userAgent.indexOf("Edge") > -1) {
    browserName = "Edge";
  } else {
    browserName = "Other";
  }

  return browserName;
}
